import { BaseViewStore } from 'core/stores';
import { ReceivingEditForm } from 'modules/receiving/forms';
import { action, observable, makeObservable } from 'mobx';
import ReceivingStore from './ReceivingStore';
import { ApplicationStore } from 'platform/application/stores';
import { ProductStore } from 'modules/product/stores';
import {
	notificationError,
	isSeller,
	isSuperAdminOrganization,
	formatDate,
	isSmartshipWarehouseAdministrator,
	isWarehouse,
	translate,
} from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, receivingStatus, receivingType, transferStatuses, sourceOfChange } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';
import moment from 'moment';
import _ from 'lodash';
import { Poller } from 'common/utils';
import { ResultReport } from 'modules/order/pages';

class ReceivingEditViewStore extends BaseViewStore {
	@observable showWarningMessage = false;
	receivingItem = null;
	receivingStatusOptions = [];
	receivingTypesOptions = [];
	@observable productOptions = [];
	initialIsReceived = [];
	selectedProducts = [];
	@observable selectedProductsToExclude = [];
	isTransfer = false;
	isSeller = false;
	isWarehouse = false;
	isReceivedStatus = false;
	isEdit = false;
	previousStatus = null;
	dateFormat = null;
	debouncedProductsSearch = null;
	throttleOnMenuClose = null;
	actualReceivingDateWithTime = null;
	@observable tempProductsOptions = [];
	@observable pageProducts = 1;
	@observable infiniteSearchPhrase = null;
	@observable isProductsDropdownLoading = false;
	@observable currentStatus = null;

	form = new ReceivingEditForm({
		onSuccess: async () => {
			await this.createOrUpdateReceiving();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.parentView = rootStore.productListViewStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.receivingStore = new ReceivingStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.isEdit = this.rootStore.routerStore.routerState.params.id != undefined;
		this.isSeller = isSeller(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.isSmartshipWarehouseAdmin = isSmartshipWarehouseAdministrator(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		this.form.clear();
		this.getLocaleData();

		if (this.isSuperAdmin || this.isSmartshipWarehouseAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore();
			await this.sellerDropdownStore.initialize({
				field: this.form.$('sellerId'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: async value => await this.clearDataOnChange(value),
			});
			this.form.$('sellerId').set('rules', 'required');
		} else {
			await this.getProductOptions();
		}

		this.warehouseToDropdown = new BaasicDropdownViewStore();
		await this.warehouseToDropdown.initialize({
			field: this.form.$('warehouseToId'),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: _.get(this, 'sellerDropdownStore.value.value'),
				embed: ['warehouseOrganizationInfo', 'warehouseOrganizationInfo.warehouseManagementType'],
			},
			onChangeCallback: async value => await this.onWarehouseChange(value, true),
			fetchInitialValues:
				(!this.isSuperAdmin && !this.isSmartshipWarehouseAdmin) ||
				_.get(this, 'sellerDropdownStore.value.value', false),
		});

		this.warehouseFromDropdown = new BaasicDropdownViewStore();
		await this.warehouseFromDropdown.initialize({
			field: this.form.$('warehouseFromId'),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: _.get(this, 'sellerDropdownStore.value.value'),
			},
			onChangeCallback: async value => await this.onWarehouseChange(value, false),
			fetchInitialValues: !this.isSuperAdmin && !this.isSmartshipWarehouseAdmin,
		});

		this.debouncedProductsSearch = _.debounce((searchPhrase, { action }) => {
			this.searchProductsAsync(searchPhrase, { action });
		}, 500);
		this.throttleOnMenuClose = _.throttle(() => this.onMenuCloseProduct(), 500);

		await this.getReceivingStatusOptions();
		await this.getReceivingTypeOptions();
		if (this.isEdit) {
			await this.findReceiving();
			this.setInitialIsReceived(this.receivingStore.item);
			if (this.receivingItem.receivingStatus.extra.abrv == receivingStatus.arrivedAtPort) {
				_.remove(this.receivingStatusOptions, item => {
					return item.extra.abrv == receivingStatus.incoming;
				});
			}
		} else {
			this.form.clear();
			this.form.$('products').add([
				{
					id: '',
					quantity: '',
					selectedProduct: null,
					description: '',
					isReceived: false,
					palletCount: 0,
					isAutomated: false,
				},
			]);
			this.form.$('receivingStatus').set(
				'value',
				_.find(this.receivingStatusOptions, item => {
					if (item.label.toLowerCase() == receivingStatus.incoming) {
						return { value: item.id, label: item.name };
					}
				})
			);
			this.form.$('receivingType').set(
				'value',
				_.find(this.receivingTypeOptions, item => {
					if (item.label.toLowerCase() == receivingType.container) {
						return { value: item.id, label: item.name };
					}
				})
			);
			this.isTransfer = false;
			this.form.$('warehouseToId').set('rules', 'required');
			if (this.isSuperAdmin || this.isSmartshipWarehouseAdmin) {
				const seller = localStorageHandler.get('selectedSeller');
				if (seller) {
					this.sellerDropdownStore.setValue(seller);
				}
			}
		}
		this.previousStatus = this.form.$('receivingStatus').value;
		this.resume();
	}

	findReceiving = async () => {
		await this.getReceiving(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'receivingStatus',
				'receivingType',
				'receivingProducts',
				'receivingProducts.product',
				'seller',
				'warehouseTo',
				'warehouseFrom',
				'seller',
			],
		});
	};

	@action.bound
	clearDataOnChange = async sellerDropdownValue => {
		await Promise.all([this.clearProductData(sellerDropdownValue), this.clearWarehouseData(sellerDropdownValue)]);
	};

	@action.bound
	clearProductData = async sellerDropdownValue => {
		if (this.isEdit) {
			this.sellerId = this.receivingItem.sellerId;
		} else {
			this.sellerId = sellerDropdownValue.value;
			this.productOptions = [];
			this.selectedProducts = [];
			this.selectedProductsToExclude = [];
			this.clearSelectedProductFields();
		}
		await this.getProductOptions();
	};

	@action.bound
	clearWarehouseData = async sellerDropdownValue => {
		if (!sellerDropdownValue) {
			sellerDropdownValue = _.get(this, 'sellerDropdownStore.value');
		}
		this.warehouseToDropdown.clear();
		this.warehouseFromDropdown.clear();
		if (sellerDropdownValue) {
			this.warehouseToDropdown.updateFilter({
				sellerWarehousesOnlyOrganizationId: sellerDropdownValue.value,
			});
			this.warehouseFromDropdown.updateFilter({
				sellerWarehousesOnlyOrganizationId: sellerDropdownValue.value,
			});
		} else if (!this.isSuperAdmin && !this.isSmartshipWarehouseAdmin) {
			Promise.all([this.warehouseToDropdown.reload(), this.warehouseFromDropdown.reload()]);
		} else {
			this.warehouseToDropdown.options = [];
			this.warehouseFromDropdown.options = [];
		}
	};

	@action.bound
	getLocaleData() {
		let locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		let localeData = moment.localeData();
		this.dateFormat = localeData.longDateFormat('L');
	}

	@action.bound
	setInitialIsReceived(receiving) {
		this.initialIsReceived = _.map(receiving.receivingProducts, item => {
			return { id: item.id, isReceived: item.isReceived, isAutomated: item.isAutomated };
		});
		_.forEach(this.form.$('products').value, item => {
			this.selectedProductsToExclude.push(item.selectedProduct.value);
		});
	}

	@action.bound
	isReceivingProductCheckboxDisabled = id => {
		const initialIsReceived = _.find(this.initialIsReceived, item => {
			return item.id == id;
		});
		if (initialIsReceived != null) {
			return initialIsReceived.isReceived;
		} else {
			return false;
		}
	};

	@action.bound
	async getReceivingStatusOptions() {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.receivingStatus);
		this.receivingStatusOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound
	async getReceivingTypeOptions() {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.receivingType);
		this.receivingTypeOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound
	getReceiving = async (id, options) => {
		await this.receivingStore.getReceiving(id, options);
		const item = this.receivingStore.item;
		this.receivingItem = this.receivingStore.item;

		item.estimatedReceivingDate = moment(item.estimatedReceivingDate)
			.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
			.format(this.dateFormat);
		if (item.actualReceivingDate != null) {
			// save date with time
			this.actualReceivingDateWithTime = item.actualReceivingDate;
			item.actualReceivingDate = formatDate(item.actualReceivingDate, true, this.dateFormat); // to get proper date inside date picker
		}

		item.receivingStatus = {
			value: item.receivingStatus.id,
			label: item.receivingStatus.name,
			extra: {
				...item.receivingStatus,
			},
		};

		item.receivingType = {
			value: item.receivingType.id,
			label: item.receivingType.name,
			extra: {
				...item.receivingType,
			},
		};
		this.form.update(item);
		if (this.isSuperAdmin || this.isSmartshipWarehouseAdmin) {
			this.sellerDropdownStore.setValue(this.receivingItem.seller);
		}
		if (item.warehouseTo) {
			this.form.$('warehouseToId').set('rules', 'required');
			this.warehouseToDropdown.setValue(item.warehouseTo);
		}
		if (item.warehouseFrom) {
			this.isTransfer = true;
			this.form.$('warehouseFromId').set('rules', 'required');
			this.form.$('cost').set('rules', 'required|numeric');
			this.warehouseFromDropdown.setValue(item.warehouseFrom);
		}

		this.previousStatus = this.form.$('receivingStatus').value;
		this.currentStatus = item.receivingStatus.extra.abrv;

		if (item.warehouseFrom) {
			this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
				return transferStatuses.indexOf(item.extra.abrv) > -1 || item.extra.abrv === receivingStatus.received;
			});
			if (this.currentStatus == receivingStatus.inTransit) {
				this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
					return item.extra.abrv != receivingStatus.pendingPickup;
				});
			}
		} else {
			this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
				return !(transferStatuses.indexOf(item.extra.abrv) > -1) || item.extra.abrv === receivingStatus.received;
			});
		}
		if (this.receivingItem.receivingStatus.extra.abrv === receivingStatus.pendingPickup) {
			_.remove(this.receivingStatusOptions, item => {
				return item.label === 'Received';
			});
		}
		_.each(item.receivingProducts, item => {
			const selectedProduct = {
				value: item.productId,
				label: item.product.mainSku,
			};
			this.form.$('products').add([
				{
					selectedProduct: selectedProduct,
					...item,
				},
			]);
			this.selectedProducts.push(selectedProduct);
			const itemIdx = _.findIndex(this.productOptions, item => {
				return item.value == selectedProduct.value;
			});
			this.productOptions.splice(itemIdx, 1);
		});
	};

	@action.bound
	async searchProductsAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageProducts = 1;
		}
		if (action === 'input-change') {
			this.isProductsDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageProducts = 1;
			this.productOptions = [];
			await this.productStore.findProduct({
				searchFields: ['mainSku'],
				search: searchPhrase || null,
				rpp: 15,
				page: 1,
				productIdsToExclude: this.selectedProductsToExclude,
				embed: ['productInventories'],
				sellerIds: this.sellerId ? [this.sellerId] : null,
				isShippable: true,
			});
			const newOptions = [];
			_.forEach(this.productStore.data, product => {
				if (!product.isMultiProduct) {
					newOptions.push({ label: product.mainSku, value: product.id });
				}
			});
			this.productOptions = newOptions;
			if (searchPhrase === '') {
				await this.getProductOptions();
			} else {
				this.productOptions = newOptions;
			}
			this.isProductsDropdownLoading = false;
		}
	}

	@action.bound
	async onMenuCloseProduct() {
		this.pageProducts = 1;
		this.productOptions = [];
		this.infiniteSearchPhrase = null;
		await this.getProductOptions();
	}

	@action.bound
	async handleProductsOnScroll() {
		if (this.productOptions.length < this.pageProducts * 15) return;
		this.isProductsDropdownLoading = true;
		this.pageProducts++;
		await this.productStore.findProduct({
			search: this.infiniteSearchPhrase || null,
			rpp: 15,
			page: this.pageProducts,
			productIdsToExclude: this.selectedProductsToExclude,
			embed: ['productInventories'],
			searchFields: ['mainSku'],
			sellerIds: this.sellerId ? [this.sellerId] : null,
		});
		_.forEach(this.productStore.data, product => {
			if (!product.isMultiProduct) {
				this.tempProductsOptions.push({ label: product.mainSku, value: product.id });
			}
		});
		if (this.tempProductsOptions.length > 0) {
			this.productOptions = this.productOptions.concat(this.tempProductsOptions);
			this.tempProductsOptions = [];
		}
		this.isProductsDropdownLoading = false;
	}

	@action.bound
	async getProductOptions() {
		await this.productStore.findProduct({
			rpp: 30,
			page: 1,
			productIdsToExclude: this.selectedProductsToExclude,
			sellerIds: this.sellerId ? [this.sellerId] : null,
			isShippable: true,
		});
		const newOptions = [];
		_.forEach(this.productStore.data, product => {
			if (!product.isMultiProduct) {
				newOptions.push({ label: product.mainSku, value: product.id });
			}
		});
		this.productOptions = newOptions;
	}

	@action.bound
	updateActualReceivingDate(isReceived) {
		//get utc datetime of right now
		this.actualReceivingDateWithTime = isReceived ? moment.utc(moment(new Date())).format() : null;
		this.form
			.$('actualReceivingDate')
			.set('value', formatDate(this.actualReceivingDateWithTime, true, this.dateFormat));
	}

	@action.bound
	createOrUpdateReceiving = async () => {
		this.suspend();
		try {
			const {
				warehouseToId,
				warehouseFromId,
				ref,
				products,
				estimatedReceivingDate,
				receivingStatus,
				receivingType,
				cost,
				isAutomated,
			} = this.form.values();
			const receivingProducts = [];
			_.forEach(products, item => {
				const receivingProduct = {
					id: item.id,
					productId: item.selectedProduct.value,
					product: { id: item.selectedProduct.value, mainSku: item.selectedProduct.label },
					quantity: item.quantity,
					description: item.description,
					isReceived: item.isReceived,
					isAutomated: item.isAutomated,
					palletCount: item.palletCount,
				};
				receivingProducts.push(receivingProduct);
			});
			let receiving = {
				ref: ref,
				receivingProducts: receivingProducts,
				warehouseToId: warehouseToId,
				warehouseFromId: warehouseFromId,
				estimatedReceivingDate: estimatedReceivingDate,
				receivingStatusId: receivingStatus.value,
				receivingTypeId: receivingType.value,
				cost: cost,
				actualReceivingDate: this.actualReceivingDateWithTime,
				isAutomated: isAutomated,
			};
			if (this.isEdit) {
				receiving.id = this.rootStore.routerStore.routerState.params.id;
				receiving.sellerId = this.receivingItem.sellerId;

				await this.receivingStore.updateReceiving(receiving, sourceOfChange.updateReceiving);
				this.rootStore.notificationStore.success([
					'CONTAINERFORM.NOTIFICATIONS.UPDATE',
					{ name: this.form.$('ref').value },
				]);
			} else {
				receiving.sellerId = this.isSuperAdmin || this.isSmartshipWarehouseAdmin ? this.sellerId : null;
				receiving.estimatedReceivingDate = moment.utc(receiving.estimatedReceivingDate, this.dateFormat);
				const response = await this.receivingStore.createReceiving(receiving, sourceOfChange.createReceiving);
				this.startPolling(response);
				this.rootStore.notificationStore.success(['CONTAINERFORM.NOTIFICATIONS.CREATE', { name: ref }]);
			}
			this.rootStore.routerStore.goBack();
		} catch (err) {
			this.resume();
			let errorMessage;
			if (
				_.get(err, 'data') === 'CONTAINERFORM.NOTIFICATIONS.MISSINGINVENTORY' ||
				_.get(err, 'data') === 'CONTAINERFORM.NOTIFICATIONS.WMSSHIPPED'
			) {
				errorMessage = _.get(err, 'data');
			} else if (_.get(err, 'data.message') === 'CONTAINERFORM.NOTIFICATIONS.INVALIDSTATUS') {
				errorMessage = _.get(err, 'data.message');
			} else {
				errorMessage = undefined;
			}
			if (errorMessage != undefined) {
				if (errorMessage === 'CONTAINERFORM.NOTIFICATIONS.WMSSHIPPED') {
					this.selectedProducts = [];
					const length = this.form.$('products').value.length;
					for (var i = 0; i < length; i++) {
						this.form.$('products').del(i);
					}
					await this.findReceiving();
				}
				this.rootStore.notificationStore.error(errorMessage);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	startPolling = response => {
		if (_.get(this, 'warehouseToDropdown.value.extra.warehouseOrganizationInfo.sendTransferAPI')) {
			const wmsName = _.get(
				this,
				'warehouseToDropdown.value.extra.warehouseOrganizationInfo.warehouseManagementType.name'
			);

			new Poller({
				pollingFn: async () => {
					return this.receivingStore.getWmsReceivingResult(_.get(response, 'id'));
				},
				onResult: this.onResultCallback,
				initialDelay: 1000,
				loadingMessage: translate(['BULKNOTIFICATION.SENDCONTAINERTOWMS', { wms: wmsName }]),
				rootStore: this.rootStore,
				message: {
					success: `The container has been created in ${wmsName}.`,
					error: `Some orders failed to send to ${wmsName}. Click for details.`,
				},
			});
		}
	};

	onResultCallback = (result, message, action) => {
		if (result) {
			if (result.total === result.success) {
				this.rootStore.notificationStore.success(message.success);
			} else {
				this.rootStore.notificationStore.error(message.error, false, {
					autoClose: false,
					onClick: () => {
						this.openModal({
							data: result,
							component: ResultReport,
							fulfillmentOrderAction: action ? action : null,
						});
					},
				});
			}
		}
	};

	@action.bound
	updateReceivingOptions(value, field) {
		if (field.value == '' || field.value == undefined) {
			this.selectedProducts.push(value);
		} else {
			if (value != null) {
				const selectedProduct = field.value;
				this.selectedProducts.splice(
					_.findIndex(this.selectedProducts, item => {
						return (item.value = selectedProduct.value);
					}),
					1
				);
				this.selectedProducts.push(value);
				this.productOptions.push(selectedProduct);
			}
		}
		this.productOptions = _.difference(this.productOptions, this.selectedProducts);
	}

	@action.bound
	onWarehouseChange = async (value, isWarehouseTo) => {
		if (isWarehouseTo) {
			await this.warehouseFromDropdown.updateFilter({
				idsToExclude: value ? [value.value] : [],
			});
		} else {
			await this.warehouseToDropdown.updateFilter({
				idsToExclude: value ? [value.value] : [],
			});
		}
	};

	@action.bound
	setReceivingStatus(status) {
		if (this.form.$('receivingStatus').value.extra.abrv != receivingStatus.received) {
			this.previousStatus = this.form.$('receivingStatus').value;
		}
		const receivingStatusItem = _.find(this.receivingStatusOptions, item => {
			return item.extra.abrv == status;
		});
		this.form.$('receivingStatus').set('value', receivingStatusItem);
	}

	@action.bound
	onReceivingStatusChange = (value, field) => {
		if (
			this.previousStatus != null &&
			this.previousStatus.value != value.value &&
			field.value.extra.abrv != receivingStatus.received
		) {
			this.previousStatus = field.value;
		}
		this.form.$(field.path).set('value', value);

		this.currentStatus = value.extra.abrv;

		if (value.label.toLowerCase() == receivingStatus.received) {
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
			this.form.$('products').each(field => {
				if (field.id.includes('isReceived')) {
					field.set('value', true);
				}
			});
		} else {
			this.showWarningMessage = false;
			this.updateActualReceivingDate(false);
			this.form.$('products').each(field => {
				if (field.id.includes('isReceived') && field.isDirty) {
					field.set('value', false);
				}
			});
		}
	};

	@action.bound
	onReceivingTypeChange = (value, field) => {
		this.clearWarehouseData();
		this.form.$(field.path).set('value', value);
		if (value.extra.abrv.includes('transfer')) {
			this.isTransfer = true;
			this.form.$('warehouseFromId').set('rules', 'required');
			this.form.$('warehouseToId').set('rules', '');
			this.form.$('cost').set('rules', 'required|numeric');
			this.setReceivingStatus(receivingStatus.pendingPickup);
		} else {
			this.isTransfer = false;
			this.form.$('warehouseFromId').set('value', null);
			this.form.$('warehouseFromId').set('rules', null);
			this.form.$('warehouseToId').set('rules', 'required');
			this.form.$('cost').set('rules', null);
			this.setReceivingStatus(receivingStatus.incoming);
		}
	};

	@action.bound
	onProductChange = (value, field) => {
		this.updateReceivingOptions(value, field);
		if (value != null) {
			if (_.find(this.selectedProducts, p => p.value === value.value)) {
				this.selectedProductsToExclude.push(value.value);
			}
		} else {
			var index = this.selectedProductsToExclude.indexOf(this.form.$(field.path).value.value);
			if (index !== -1) {
				this.selectedProductsToExclude.splice(index, 1);
			}
		}
		this.form.$(field.path).set('value', value);
	};

	@action.bound
	clearSelectedProductFields = () => {
		this.form.$('products').each(field => {
			if (field.id.includes('selectedProduct')) {
				if (field.value) field.set('value', null);
			}
		});
	};

	@action.bound
	addProduct = () => {
		this.form.$('products').add([
			{
				id: '',
				quantity: '',
				selectedProduct: null,
				description: '',
				isReceived: false,
				isAutomated: false,
				palletCount: 0,
			},
		]);
		if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
			this.form.$('receivingStatus').set('value', this.previousStatus);
			this.showWarningMessage = false;
			this.updateActualReceivingDate(false);
		}
		// _.forEach(this.form.$('products').value, item => {
		// 	this.selectedProductsToExclude.push(item.selectedProduct.value);
		// });
	};

	@action.bound
	checkReceivingProducts() {
		const receivingProduct = _.find(this.form.$('products').value, item => {
			return item.isReceived == false;
		});
		if (receivingProduct == undefined) {
			this.setReceivingStatus(receivingStatus.received);
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
		} else {
			if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
				this.form.$('receivingStatus').set('value', this.previousStatus);
			}
			if (this.previousStatus.extra.abrv != receivingStatus.received) {
				this.showWarningMessage = false;
				this.updateActualReceivingDate(false);
			} else {
				this.showWarningMessage = true;
				this.updateActualReceivingDate(true);
			}
		}
	}

	@action.bound
	onDelete = (e, field) => {
		if (field.value.selectedProduct != '' && field.value.value != null) {
			const { selectedProduct } = field.value;
			this.selectedProducts.splice(
				_.findIndex(this.selectedProducts, item => {
					return item.value === selectedProduct.value;
				}),
				1
			);
			this.productOptions.push(selectedProduct);
		} else {
			var index = this.selectedProductsToExclude.indexOf(field.value.selectedProduct.value);
			if (index !== -1) {
				this.selectedProductsToExclude.splice(index, 1);
			}
			this.getProductOptions();
		}
		if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
			const receivingProduct = _.find(this.form.$('products').value, item => {
				return item.isReceived == false;
			});
			if (receivingProduct != undefined) {
				this.form.$('receivingStatus').set('value', this.previousStatus);
			} else {
				this.setReceivingStatus(receivingStatus.received);
			}
		}
		this.form.$(field.path).onDel(e);
		const receivingProducts = this.form.$('products').value;
		const receivingProduct = _.find(receivingProducts, item => {
			return item.isReceived == false;
		});
		if (receivingProduct == undefined && receivingProducts != undefined && receivingProducts.length > 0) {
			this.setReceivingStatus(receivingStatus.received);
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
		}
	};
}

export default ReceivingEditViewStore;
